import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { BookingWidget } from '../components/BookingWidget';
import { FAQSection } from '../components/FAQSection';
import { MapPin, Phone, Mail, Clock, MessageCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { SEOHead, BreadcrumbSchema, SpeakableSchema } from '../components/SEO';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [settings, setSettings] = useState(null);

  // Fetch site settings for contact info and maps
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/site-settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Use settings or defaults
  const contactInfo = settings ? [
    {
      icon: MapPin,
      title: 'Address',
      content: `The Hotel Emerald\n${settings.contact?.address || 'NALCO Chowk, Semiliguda, Koraput, Odisha 764036, India'}`,
    },
    {
      icon: Phone,
      title: 'Phone',
      content: (settings.contact?.phones || ['+91 6372 350 065', '+91 9437 082 665']).join('\n'),
    },
    {
      icon: Mail,
      title: 'Email',
      content: `${settings.contact?.email || 'thehotelemerald@gmail.com'}${settings.contact?.secondary_email ? '\n' + settings.contact.secondary_email : ''}`,
    },
    {
      icon: Clock,
      title: 'Reception Hours',
      content: settings.contact?.reception_hours || '24 hours, 7 days a week',
    },
  ] : [
    { icon: MapPin, title: 'Address', content: 'The Hotel Emerald\nNALCO Chowk, Semiliguda\nKoraput, Odisha 764036, India' },
    { icon: Phone, title: 'Phone', content: '+91 6372 350 065\n+91 9437 082 665' },
    { icon: Mail, title: 'Email', content: 'thehotelemerald@gmail.com\nreservations@thehotelemerald.in' },
    { icon: Clock, title: 'Reception Hours', content: '24 hours, 7 days a week' },
  ];

  const mapEmbedUrl = settings?.google_maps?.embed_url || 
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d121726.77947553752!2d82.82!3d18.87!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a3ad9c6d8c3c7e1%3A0x9e8c7c7c7c7c7c7c!2sSemiliguda%2C%20Odisha!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin";
  
  const directionsUrl = settings?.google_maps?.directions_url || 
    `https://www.google.com/maps/dir/?api=1&destination=${settings?.google_maps?.latitude || '18.87'},${settings?.google_maps?.longitude || '82.82'}`;

  const whatsappNumber = settings?.contact?.whatsapp || '+916372350065';
  const primaryPhone = settings?.contact?.phones?.[0] || '+916372350065';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/inquiries`, {
        ...formData,
        inquiry_type: 'general'
      });
      toast.success('Message sent! We\'ll get back to you shortly.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}?text=Hello! I would like to inquire about booking at The Hotel Emerald.`, '_blank');
  };

  const breadcrumbs = [
    { name: 'Home', url: 'https://thehotelemerald.in/' },
    { name: 'Contact', url: 'https://thehotelemerald.in/contact' }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="contact-page">
      <SEOHead 
        title="Contact Us | The Hotel Emerald - Get in Touch"
        description="Contact The Hotel Emerald for reservations, inquiries, or assistance. Located in Semiliguda, Odisha. Call +91-6372-350-065 or email thehotelemerald@gmail.com"
        keywords="contact hotel emerald, hotel semiliguda contact, odisha hotel booking"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <SpeakableSchema cssSelector={[".speakable-content", "h1", "h2"]} />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[40vh] min-h-[350px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/gallery/hotel-25.jpeg"
            alt="Hotel Emerald Lobby"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-emerald-950/60" />
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="overline text-gold mb-3"
          >
            Get In Touch
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-serif text-4xl md:text-5xl lg:text-6xl mb-4"
          >
            Contact Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-emerald-100 max-w-xl mx-auto"
          >
            We're here to assist you with your stay
          </motion.p>
        </div>
      </section>

      {/* Booking Widget */}
      <div className="container-custom -mt-16 relative z-20">
        <BookingWidget variant="compact" />
      </div>

      {/* Contact Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">Reach Out</p>
              <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-8">
                We'd Love to Hear From You
              </h2>

              <div className="space-y-6 mb-8">
                {contactInfo.map((info) => (
                  <div key={info.title} className="flex gap-4" data-testid={`contact-info-${info.title.toLowerCase()}`}>
                    <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/50 rounded-sm flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-emerald-700 dark:text-emerald-400" />
                    </div>
                    <div>
                      <h4 className="font-serif text-foreground mb-1">{info.title}</h4>
                      <p className="text-muted-foreground text-sm whitespace-pre-line">{info.content}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* WhatsApp Button */}
              <Button
                onClick={openWhatsApp}
                className="bg-green-600 hover:bg-green-700 text-white w-full md:w-auto"
                data-testid="whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Chat on WhatsApp
              </Button>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleSubmit} className="bg-card border border-border rounded-sm p-6 md:p-8" data-testid="contact-form">
                <h3 className="font-serif text-2xl text-foreground mb-6">Send a Message</h3>
                
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        data-testid="contact-form-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                        data-testid="contact-form-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Phone (Optional)</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        data-testid="contact-form-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subject">Subject</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        required
                        data-testid="contact-form-subject"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="min-h-[120px]"
                      required
                      placeholder="How can we help you?"
                      data-testid="contact-form-message"
                    />
                  </div>

                  <Button type="submit" className="btn-emerald w-full" disabled={isSubmitting} data-testid="contact-form-submit">
                    <Send className="w-4 h-4 mr-2" />
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-padding bg-muted/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">Find Us</p>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground speakable-content">
              Our Location
            </h2>
          </motion.div>

          <div className="rounded-sm overflow-hidden h-[450px] border border-border" data-testid="contact-map">
            <iframe
              src={mapEmbedUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Hotel Location Map"
            />
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold inline-flex items-center"
              data-testid="get-directions-btn"
            >
              <MapPin className="w-4 h-4 mr-2" />
              Get Directions
            </a>
            <a
              href={`tel:${primaryPhone.replace(/\s/g, '')}`}
              className="btn-emerald inline-flex items-center"
              data-testid="call-now-btn"
            >
              <Phone className="w-4 h-4 mr-2" />
              Call Now
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection title="Frequently Asked Questions" />

      <Footer />
    </div>
  );
}
